/**
 * Money the shop owes its suppliers.
 *
 * The mirror of "Money owed": that screen is people who owe the shop, this one
 * is deliveries the shop has taken and not finished paying for.
 *
 * Paying a supplier is rarely one event. Half now, the rest at the end of the
 * month, and a delivery can sit part-paid for weeks - so an invoice is not a
 * tick but a running balance: what it came to, what has been paid against it,
 * and what is still on it. Every payment is kept, with the day the money
 * actually left, so the invoice can always say how it got where it is.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { compareSizes, dayKey } from '@pos/shared';
import { useI18n, usePlural, useT } from '../../core/i18n/index.js';
import { useSettings } from '../../core/state/settings.js';
import { useAuth } from '../../core/state/auth.js';
import { useToast } from '../../core/state/toast.js';
import { api } from '../../core/api.js';
import { Button, Confirm, Empty, Field, Loading, Modal, MoneyInput, Pill, Swatch, Tabs } from '../../core/ui/ui.js';

interface Bill {
  id: string;
  supplier_id: string | null;
  supplier_name: string | null;
  invoice_no: string | null;
  total: number;
  paid_amount: number;
  paid: number;
  line_count: number;
  created_at: string;
}

interface Payment {
  id: string;
  amount: number;
  method: string;
  note: string | null;
  paid_on: string;
  user_name: string | null;
}

interface Line {
  id: string;
  qty: number;
  unit_cost: number;
  size: string | null;
  name_en: string | null;
  name_ar: string | null;
  color_en: string | null;
  color_ar: string | null;
  color_hex: string | null;
}

const left = (b: { total: number; paid_amount: number }) => b.total - b.paid_amount;

/** How long an unpaid invoice has been sitting there. */
function daysSince(iso: string): number {
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
}

/** Paid in full, partly paid, or nothing paid at all. */
function StatusPill({ bill }: { bill: Bill }) {
  const t = useT();
  if (left(bill) <= 0) return <Pill kind="ok">{t('bills.paid')}</Pill>;
  if (bill.paid_amount > 0) return <Pill kind="info">{t('bills.partlyPaid')}</Pill>;
  return <Pill kind="warn">{t('bills.unpaid')}</Pill>;
}

// ---------------------------------------------------------------------------
// The list
// ---------------------------------------------------------------------------

export function Bills() {
  const t = useT();
  const plural = usePlural();
  const { money, date } = useSettings();
  const navigate = useNavigate();
  const toast = useToast();

  const [rows, setRows] = useState<Bill[] | null>(null);
  const [tab, setTab] = useState<'open' | 'all'>('open');

  const load = useCallback(async () => {
    try {
      setRows(await api<Bill[]>('/stock/purchases'));
    } catch (e) {
      toast.err((e as Error).message);
      setRows([]);
    }
  }, [toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const open = useMemo(() => (rows ?? []).filter((r) => left(r) > 0), [rows]);
  const owed = useMemo(() => open.reduce((a, r) => a + left(r), 0), [open]);

  // Oldest debt first - that is the one the supplier will ring about.
  const shown = useMemo(() => {
    const list = tab === 'open' ? open : (rows ?? []);
    return [...list].sort((a, b) => {
      const byOpen = Number(left(b) > 0) - Number(left(a) > 0);
      return byOpen || a.created_at.localeCompare(b.created_at);
    });
  }, [tab, open, rows]);

  if (!rows) return <Loading />;

  return (
    <div className="col">
      <div className="grid c4">
        <div className={`kpi ${owed > 0 ? 'accent' : ''}`}>
          <div className="k">{t('bills.totalOwed')}</div>
          <div className="v tnum">{money(owed)}</div>
          <div className="d">
            {plural(open.length, {
              one: 'bills.invoiceOne',
              two: 'bills.invoiceTwo',
              few: 'bills.invoiceFew',
              many: 'bills.invoiceMany',
            })}
          </div>
        </div>
      </div>

      <Tabs
        tabs={[
          { key: 'open' as const, label: t('bills.stillToPay'), count: open.length },
          { key: 'all' as const, label: t('common.all'), count: rows.length },
        ]}
        active={tab}
        onChange={setTab}
      />

      {shown.length === 0 ? (
        <Empty
          icon={tab === 'open' ? '✅' : '📄'}
          title={tab === 'open' ? t('bills.allSettled') : t('bills.none')}
          hint={tab === 'open' ? t('bills.allSettledHint') : t('bills.noneHint')}
        />
      ) : (
        <div className="card flush">
          <div className="table-wrap">
            <table className="cards">
              <thead>
                <tr>
                  <th>{t('common.supplier')}</th>
                  <th>{t('stock.invoiceNo')}</th>
                  <th>{t('common.date')}</th>
                  <th className="num">{t('common.total')}</th>
                  <th className="num">{t('bills.paidSoFar')}</th>
                  <th className="num">{t('bills.left')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => {
                  const age = daysSince(r.created_at);
                  const owing = left(r);
                  return (
                    <tr key={r.id}>
                      <td className="full strong" data-label={t('common.supplier')}>
                        {r.supplier_name ?? <span className="muted">{t('bills.noSupplier')}</span>}
                      </td>
                      <td data-label={t('stock.invoiceNo')}>
                        {r.invoice_no ?? <span className="muted">—</span>}
                      </td>
                      <td data-label={t('common.date')}>
                        {date(r.created_at)}{' '}
                        {owing > 0 && age > 30 ? (
                          <Pill kind={age > 60 ? 'danger' : 'warn'}>{t('common.days', { n: age })}</Pill>
                        ) : null}
                      </td>
                      <td className="num tnum" data-label={t('common.total')}>{money(r.total)}</td>
                      <td className="num tnum" data-label={t('bills.paidSoFar')}>
                        {r.paid_amount > 0 ? money(r.paid_amount) : <span className="muted">—</span>}
                      </td>
                      <td className="num tnum strong" data-label={t('bills.left')}>
                        {owing > 0 ? money(owing) : <StatusPill bill={r} />}
                      </td>
                      <td className="num">
                        <Button size="sm" variant={owing > 0 ? 'primary' : 'default'} onClick={() => navigate(`/bills/${r.id}`)}>
                          {t('bills.openInvoice')}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// One invoice, opened up
// ---------------------------------------------------------------------------

export function BillDetail() {
  const { id } = useParams<{ id: string }>();
  const t = useT();
  const { lang } = useI18n();
  const { money, date } = useSettings();
  const { can } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  const [data, setData] = useState<{ purchase: Bill & { supplier_phone: string | null }; lines: Line[]; payments: Payment[] } | null>(null);
  const [paying, setPaying] = useState(false);
  const [removing, setRemoving] = useState<Payment | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await api(`/stock/purchases/${id}`));
    } catch (e) {
      toast.err((e as Error).message);
    }
  }, [id, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const b = data?.purchase;
  const owing = b ? left(b) : 0;
  const pieces = (data?.lines ?? []).reduce((a, l) => a + l.qty, 0);
  const nm = (l: Line) => (lang === 'ar' ? l.name_ar || l.name_en : l.name_en || l.name_ar) ?? '—';
  const col = (l: Line) => (lang === 'ar' ? l.color_ar || l.color_en : l.color_en || l.color_ar);

  const groups = useMemo(() => {
    const by = new Map<string, { key: string; name: string; rows: Line[]; pieces: number; total: number }>();
    for (const l of data?.lines ?? []) {
      const key = `${l.name_en ?? ''}|${l.name_ar ?? ''}`;
      let g = by.get(key);
      if (!g) {
        g = { key, name: nm(l), rows: [], pieces: 0, total: 0 };
        by.set(key, g);
      }
      g.rows.push(l);
      g.pieces += l.qty;
      g.total += l.qty * l.unit_cost;
    }
    const out = [...by.values()];
    for (const g of out) g.rows.sort((a, b) => compareSizes(a.size ?? '', b.size ?? '') || (a.color_en ?? '').localeCompare(b.color_en ?? ''));
    return out.sort((a, b) => a.name.localeCompare(b.name));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.lines, lang]);

  if (!data || !b) return <Loading />;

  const removePayment = async (p: Payment) => {
    try {
      await api(`/stock/purchases/${id}/payments/${p.id}`, { method: 'DELETE' });
      toast.ok(t('bills.paymentRemoved'));
      setRemoving(null);
      await load();
    } catch (e) {
      toast.err((e as Error).message);
    }
  };

  return (
    <div className="col">
      <div className="card">
        <div className="card-head">
          <div>
            <h2>{b.invoice_no ?? t('bills.noInvoiceNo')}</h2>
            <div className="small muted">
              {b.supplier_id ? (
                <a href={`/suppliers/${b.supplier_id}`} onClick={(e) => { e.preventDefault(); navigate(`/suppliers/${b.supplier_id}`); }}>
                  {b.supplier_name}
                </a>
              ) : (
                t('bills.noSupplier')
              )}
              {' · '}{date(b.created_at)}
            </div>
          </div>
          <div className="spacer" />
          <Button variant="ghost" onClick={() => navigate('/bills')}>{t('common.back')}</Button>
          {can('purchase.edit') && owing > 0 ? (
            <Button variant="primary" onClick={() => setPaying(true)}>{t('bills.recordPayment')}</Button>
          ) : null}
        </div>

        <div className="row wrap">
          <Pill>{t('common.total')}: {money(b.total)}</Pill>
          <Pill kind={b.paid_amount > 0 ? 'ok' : undefined}>{t('bills.paidSoFar')}: {money(b.paid_amount)}</Pill>
          <Pill kind={owing > 0 ? 'warn' : 'ok'}>{t('bills.left')}: {money(owing)}</Pill>
          <StatusPill bill={b} />
        </div>
      </div>

      {/* The payments, oldest first, so the invoice explains how it got here. */}
      <div className="card">
        <div className="card-head">
          <h3>{t('bills.payments')}</h3>
          <div className="spacer" />
        </div>
        {data.payments.length === 0 ? (
          <Empty icon="💸" title={t('bills.noPayments')} hint={t('bills.noPaymentsHint')} />
        ) : (
          <div className="recv-list">
            {data.payments.map((p) => (
              <div key={p.id} className="recv-variant">
                <span className="grow">
                  <span className="strong tnum">{money(p.amount)}</span>{' '}
                  <span className="muted">{t(`bills.method_${p.method}` as 'bills.method_cash')}</span>
                  <div className="xsmall muted">
                    {date(p.paid_on)}
                    {p.user_name ? ` · ${p.user_name}` : ''}
                    {p.note ? ` · ${p.note}` : ''}
                  </div>
                </span>
                {can('purchase.edit') ? (
                  <Button size="sm" variant="ghost" onClick={() => setRemoving(p)}>
                    {t('common.delete')}
                  </Button>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </div>

      {/*
        * What actually came in the box - gathered under the style it belongs
        * to, the same way the delivery was entered. 268 rows of "Ankle boots
        * 36 / 38 / 40" read as a wall; one block per style with the sizes
        * inside it reads like the invoice did.
        */}
      <div className="card">
        <div className="card-head">
          <h3>{t('bills.whatCameIn')}</h3>
          <div className="spacer" />
          <span className="xsmall muted">{t('bills.piecesLines', { pieces, lines: data.lines.length })}</span>
        </div>
        <div className="recv-list">
          {groups.map((g) => (
            <div key={g.key} className="recv-style">
              <div className="recv-style-head">
                <div className="grow">
                  <div className="strong">{g.name}</div>
                  <div className="xsmall muted">
                    {t('bills.piecesLines', { pieces: g.pieces, lines: g.rows.length })}
                    {' · '}{t('stock.unitCost')} {money(g.rows[0]!.unit_cost)}
                  </div>
                </div>
                <div className="recv-style-total tnum strong">{money(g.total)}</div>
              </div>
              <div className="recv-variants">
                {g.rows.map((l) => (
                  <div key={l.id} className="recv-variant">
                    <span className="grow">
                      <span className="strong">{l.size}</span>{' '}
                      {l.color_hex ? <Swatch hex={l.color_hex} /> : null}{' '}
                      <span className="muted">{col(l)}</span>
                    </span>
                    <span className="tnum">{l.qty} × {money(l.unit_cost)}</span>
                    <span className="tnum strong" style={{ minWidth: '6rem', textAlign: 'end' }}>{money(l.qty * l.unit_cost)}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {paying ? (
        <PaymentDialog
          purchaseId={b.id}
          outstanding={owing}
          supplier={b.supplier_name ?? t('bills.noSupplier')}
          onClose={() => setPaying(false)}
          onPaid={async () => {
            setPaying(false);
            await load();
          }}
        />
      ) : null}

      {removing ? (
        <Confirm
          title={t('bills.removePayment')}
          message={t('bills.removePaymentConfirm', { amount: money(removing.amount) })}
          confirmLabel={t('common.delete')}
          danger
          onConfirm={() => void removePayment(removing)}
          onCancel={() => setRemoving(null)}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Recording a payment
// ---------------------------------------------------------------------------

const METHODS = ['cash', 'bank', 'cheque', 'card'] as const;

function PaymentDialog({
  purchaseId,
  outstanding,
  supplier,
  onClose,
  onPaid,
}: {
  purchaseId: string;
  outstanding: number;
  supplier: string;
  onClose: () => void;
  onPaid: () => Promise<void>;
}) {
  const t = useT();
  const { money } = useSettings();
  const toast = useToast();
  // Most payments settle the invoice, so that is what the box opens on.
  const [amount, setAmount] = useState(outstanding);
  const [method, setMethod] = useState<string>('cash');
  const [note, setNote] = useState('');
  const [paidOn, setPaidOn] = useState(() => dayKey(new Date()));
  const [busy, setBusy] = useState(false);

  const tooMuch = amount > outstanding;

  const save = async () => {
    setBusy(true);
    try {
      await api(`/stock/purchases/${purchaseId}/payments`, {
        body: { amount, method, note: note.trim() || null, paidOn },
      });
      toast.ok(t('bills.paymentRecorded'));
      await onPaid();
    } catch (e) {
      toast.err((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={t('bills.recordPayment')}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <div className="spacer" />
          <Button variant="primary" busy={busy} disabled={amount <= 0 || tooMuch} onClick={() => void save()}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="col">
        <p className="small muted">{t('bills.payingTo', { supplier, amount: money(outstanding) })}</p>

        <Field
          label={t('bills.amountPaid')}
          error={tooMuch ? t('bills.tooMuch', { amount: money(outstanding) }) : undefined}
        >
          <MoneyInput className="big" value={amount} onValue={setAmount} />
        </Field>

        {amount !== outstanding && outstanding > 0 ? (
          <Button size="sm" onClick={() => setAmount(outstanding)}>
            {t('bills.payTheRest', { amount: money(outstanding) })}
          </Button>
        ) : null}

        <div className="field">
          <label>{t('bills.howPaid')}</label>
          <div className="btn-row">
            {METHODS.map((m) => (
              <Button
                key={m}
                size="sm"
                variant={method === m ? 'primary' : 'default'}
                onClick={() => setMethod(m)}
              >
                {t(`bills.method_${m}` as 'bills.method_cash')}
              </Button>
            ))}
          </div>
        </div>

        <Field label={t('bills.paidOn')} hint={t('bills.paidOnHint')}>
          <input type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} />
        </Field>

        <Field label={t('common.notes')}>
          <input value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// A supplier's account
// ---------------------------------------------------------------------------

export function SupplierAccount() {
  const { id } = useParams<{ id: string }>();
  const t = useT();
  const { money, date } = useSettings();
  const toast = useToast();
  const navigate = useNavigate();

  const [data, setData] = useState<{
    supplier: { id: string; name: string; phone: string | null; notes: string | null };
    invoices: Bill[];
    billed: number;
    paid: number;
    owed: number;
  } | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        setData(await api(`/stock/suppliers/${id}/account`));
      } catch (e) {
        toast.err((e as Error).message);
      }
    })();
  }, [id, toast]);

  if (!data) return <Loading />;

  return (
    <div className="col">
      <div className="card">
        <div className="card-head">
          <div>
            <h2>{data.supplier.name}</h2>
            {data.supplier.phone ? <div className="small muted" dir="ltr">{data.supplier.phone}</div> : null}
          </div>
          <div className="spacer" />
          <Button variant="ghost" onClick={() => navigate('/bills')}>{t('common.back')}</Button>
        </div>
        {data.supplier.notes ? <p className="small muted">{data.supplier.notes}</p> : null}
      </div>

      <div className="grid c3">
        <div className="kpi">
          <div className="k">{t('bills.billedTotal')}</div>
          <div className="v tnum">{money(data.billed)}</div>
        </div>
        <div className="kpi">
          <div className="k">{t('bills.paidSoFar')}</div>
          <div className="v tnum">{money(data.paid)}</div>
        </div>
        <div className={`kpi ${data.owed > 0 ? 'accent' : ''}`}>
          <div className="k">{t('bills.left')}</div>
          <div className="v tnum">{money(data.owed)}</div>
        </div>
      </div>

      {data.invoices.length === 0 ? (
        <Empty icon="📄" title={t('bills.none')} hint={t('bills.noneHint')} />
      ) : (
        <div className="card flush">
          <div className="table-wrap">
            <table className="cards">
              <thead>
                <tr>
                  <th>{t('stock.invoiceNo')}</th>
                  <th>{t('common.date')}</th>
                  <th className="num">{t('common.items')}</th>
                  <th className="num">{t('common.total')}</th>
                  <th className="num">{t('bills.left')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.invoices.map((r) => (
                  <tr key={r.id}>
                    <td className="full strong" data-label={t('stock.invoiceNo')}>
                      {r.invoice_no ?? <span className="muted">—</span>}
                    </td>
                    <td data-label={t('common.date')}>{date(r.created_at)}</td>
                    <td className="num" data-label={t('common.items')}>{r.line_count}</td>
                    <td className="num tnum" data-label={t('common.total')}>{money(r.total)}</td>
                    <td className="num tnum strong" data-label={t('bills.left')}>
                      {left(r) > 0 ? money(left(r)) : <StatusPill bill={r} />}
                    </td>
                    <td className="num">
                      <Button size="sm" onClick={() => navigate(`/bills/${r.id}`)}>
                        {t('bills.openInvoice')}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
